import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Shield, Users, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { authAPI, patientAPI } from '../utils/api';

export default function LandingPage({ onLogin }) {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [role, setRole] = useState('patient');
  const [doctors, setDoctors] = useState([]);
  const [cmsSettings, setCmsSettings] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hoveredDoctor, setHoveredDoctor] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    consent_accepted: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDoctors();
    fetchCMSSettings();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        const maxSlides = Math.ceil(doctors.length / 3);
        return (prev + 1) % Math.max(1, maxSlides);
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [doctors.length]);

  const fetchDoctors = async () => {
    try {
      // Fetch without auth - use axios directly
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/patient/doctors`);
      const data = await response.json();
      setDoctors(data.slice(0, 6));
    } catch (error) {
      console.log('Doctors not loaded:', error);
    }
  };

  const fetchCMSSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/settings`);
      const data = await response.json();
      setCmsSettings(data);
    } catch (error) {
      console.log('CMS settings not loaded:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && !formData.consent_accepted) {
      toast.error('Пожалуйста, примите условия согласия');
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? authAPI.login : authAPI.signup;
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { ...formData, role };

      const response = await endpoint(payload);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      toast.success(isLogin ? 'Добро пожаловать!' : 'Аккаунт успешно создан!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка аутентификации');
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    const maxSlides = Math.ceil(doctors.length / 3);
    setCurrentSlide((prev) => (prev + 1) % maxSlides);
  };

  const prevSlide = () => {
    const maxSlides = Math.ceil(doctors.length / 3);
    setCurrentSlide((prev) => (prev - 1 + maxSlides) % maxSlides);
  };

  if (showAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-white rounded-2xl shadow-xl border border-slate-100">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-2">
              {isLogin ? 'Добро пожаловать' : 'Создать аккаунт'}
            </h2>
            <p className="text-slate-600">
              {isLogin ? 'Войдите в свой аккаунт' : 'Присоединяйтесь к нашей медицинской платформе'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first_name" className="text-slate-900 font-medium">Имя*</Label>
                    <Input
                      id="first_name"
                      data-testid="first-name-input"
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required={!isLogin}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-slate-900 font-medium">Фамилия*</Label>
                    <Input
                      id="last_name"
                      data-testid="last-name-input"
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required={!isLogin}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-slate-900 font-medium">Телефон*</Label>
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required={!isLogin}
                    className="mt-1"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-slate-900 font-medium">Электронная почта*</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-900 font-medium">Пароль*</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <Label className="text-slate-900 font-medium mb-2 block">Роль*</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'patient', label: 'Пациент' },
                      { value: 'doctor', label: 'Доктор' },
                      { value: 'admin', label: 'Админ' }
                    ].map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        data-testid={`role-${r.value}-button`}
                        onClick={() => setRole(r.value)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          role === r.value
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-start space-x-2 p-3 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="consent"
                    data-testid="consent-checkbox"
                    checked={formData.consent_accepted}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent_accepted: checked })}
                    className="mt-1"
                  />
                  <label htmlFor="consent" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                    Я прочитал и принимаю <a href="#" className="text-teal-600 hover:underline">условия использования</a> и <a href="#" className="text-teal-600 hover:underline">политику конфиденциальности</a>. Я согласен на обработку моих персональных данных для предоставления медицинских услуг.
                  </label>
                </div>
              </>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full bg-teal-600 text-white hover:bg-teal-700 h-11 rounded-lg font-medium shadow-md hover:shadow-lg transition-all"
            >
              {loading ? 'Обработка...' : isLogin ? 'Войти' : 'Создать аккаунт'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-button"
              onClick={() => {
                setIsLogin(!isLogin);
                setFormData({ ...formData, consent_accepted: false });
              }}
              className="text-teal-600 hover:underline text-sm font-medium"
            >
              {isLogin ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
            </button>
          </div>

          <button
            data-testid="back-to-landing-button"
            onClick={() => setShowAuth(false)}
            className="mt-4 text-slate-500 hover:text-slate-700 text-sm w-full"
          >
            ← Вернуться на главную
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {cmsSettings?.logo_url ? (
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${cmsSettings.logo_url}`}
                alt="Logo"
                className="h-10 object-contain"
              />
            ) : (
              <>
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold font-heading text-slate-900">www.vtoroemnenie.com</span>
              </>
            )}
          </div>
          <Button
            data-testid="get-started-button"
            onClick={() => setShowAuth(true)}
            className="bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-6 shadow-sm hover:shadow-md transition-all"
          >
            Начать
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-teal-50/30 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold font-heading text-slate-900 leading-tight mb-6">
                {cmsSettings?.hero_title || 'Экспертное второе мнение по радиологии'}
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                {cmsSettings?.hero_subtitle || 'Свяжитесь с лицензированными специалистами-радиологами для получения профессионального второго мнения по медицинским снимкам. Безопасно, конфиденциально и клинически точно.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="hero-cta-button"
                  onClick={() => setShowAuth(true)}
                  className="bg-teal-600 text-white hover:bg-teal-700 h-14 px-8 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  Запросить второе мнение
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('doctors')?.scrollIntoView({ behavior: 'smooth' })}
                  className="h-14 px-8 rounded-lg text-lg font-semibold border-2 border-teal-600 text-teal-600 hover:bg-teal-50"
                >
                  Наши специалисты
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-teal-600 rounded-3xl transform rotate-6"></div>
              <img
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Медицинский специалист"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Пациентов' },
              { value: '50+', label: 'Специалистов' },
              { value: '24ч', label: 'Срочный ответ' },
              { value: '98%', label: 'Удовлетворенность' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-bold text-teal-600 mb-2">{stat.value}</div>
                <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctors Slider */}
      {doctors.length > 0 && (
        <section id="doctors" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold font-heading text-slate-900 mb-4">Наши специалисты</h2>
              <p className="text-xl text-slate-600">Лицензированные радиологи с многолетним опытом</p>
            </div>
            <div className="relative">
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {Array.from({ length: Math.ceil(doctors.length / 3) }).map((_, slideIndex) => (
                    <div key={slideIndex} className="w-full flex-shrink-0">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-4">
                        {doctors.slice(slideIndex * 3, slideIndex * 3 + 3).map((doctor, idx) => (
                          <div
                            key={idx}
                            className="relative group"
                            onMouseEnter={() => setHoveredDoctor(doctor)}
                            onMouseLeave={() => setHoveredDoctor(null)}
                          >
                            <Card className="p-6 bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-100 shadow-lg hover:shadow-2xl transition-all overflow-hidden">
                              <div className="relative">
                                <div className="w-full h-64 rounded-xl overflow-hidden shadow-xl mb-4">
                                  {doctor.photo_url ? (
                                    <img 
                                      src={`${process.env.REACT_APP_BACKEND_URL}${doctor.photo_url}`}
                                      alt={`${doctor.first_name} ${doctor.last_name}`}
                                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                                      <Users className="w-24 h-24 text-white" />
                                    </div>
                                  )}
                                </div>
                                <div className="text-center">
                                  <h3 className="text-xl font-bold text-slate-900 mb-1">
                                    {doctor.first_name} {doctor.last_name}
                                  </h3>
                                  <p className="text-md text-teal-600 font-semibold mb-2">{doctor.specialty}</p>
                                  <p className="text-sm text-slate-600">{doctor.experience_years} лет опыта</p>
                                  <div className="mt-3">
                                    <span className="text-2xl font-bold text-teal-600">${doctor.price_per_case}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Hover Popup */}
                              {hoveredDoctor?.id === doctor.id && (
                                <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-2xl p-6 flex flex-col justify-center animate-in">
                                  <h4 className="text-xl font-bold text-slate-900 mb-3">
                                    {doctor.first_name} {doctor.last_name}
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <p className="text-teal-600 font-semibold">{doctor.specialty}</p>
                                    <p className="text-slate-600"><strong>Опыт:</strong> {doctor.experience_years} лет</p>
                                    <p className="text-slate-600"><strong>Стоимость:</strong> ${doctor.price_per_case}</p>
                                    {doctor.bio && (
                                      <p className="text-slate-600 mt-3 leading-relaxed">{doctor.bio}</p>
                                    )}
                                  </div>
                                  <Button
                                    onClick={() => setShowAuth(true)}
                                    className="mt-4 bg-teal-600 text-white hover:bg-teal-700 rounded-lg w-full"
                                  >
                                    Записаться
                                  </Button>
                                </div>
                              )}
                            </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {Math.ceil(doctors.length / 3) > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-all z-10"
                  >
                    <ChevronLeft className="w-6 h-6 text-slate-900" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-all z-10"
                  >
                    <ChevronRight className="w-6 h-6 text-slate-900" />
                  </button>
                  <div className="flex justify-center gap-2 mt-8">
                    {Array.from({ length: Math.ceil(doctors.length / 3) }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          currentSlide === idx ? 'bg-teal-600 w-8' : 'bg-slate-300'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold font-heading text-slate-900 text-center mb-16">
            Почему выбирают нас
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-3">
                Безопасно и конфиденциально
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Ваши медицинские данные зашифрованы и обрабатываются в соответствии с GDPR/KVKK. Полная конфиденциальность гарантирована.
              </p>
            </Card>

            <Card className="p-8 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-3">
                Лицензированные специалисты
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Все радиологи - проверенные, лицензированные специалисты с многолетним клиническим опытом.
              </p>
            </Card>

            <Card className="p-8 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FileText className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-3">
                Подробные отчеты
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Получите исчерпывающие PDF-отчеты с результатами, заключениями и клиническими рекомендациями.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold font-heading text-slate-900 text-center mb-16">
            Как это работает
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: 1, title: 'Загрузить снимки', desc: 'Безопасно загрузите ваши радиологические снимки' },
              { step: 2, title: 'Выбрать врача', desc: 'Выберите из наших проверенных специалистов' },
              { step: 3, title: 'Оплата', desc: 'Безопасная оплата перед началом проверки' },
              { step: 4, title: 'Получить отчет', desc: 'Скачайте ваш подробный PDF-отчет' },
            ].map((item) => (
              <div key={item.step} className="text-center relative">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-700 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold font-heading text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold font-heading mb-6">Готовы получить второе мнение?</h2>
          <p className="text-xl mb-8 text-teal-50">Присоединяйтесь к сотням пациентов, которые доверяют нашим специалистам</p>
          <Button
            onClick={() => setShowAuth(true)}
            className="bg-white text-teal-600 hover:bg-slate-50 h-14 px-8 rounded-lg text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
          >
            Начать прямо сейчас
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-6 h-6 text-teal-600" />
                <span className="text-xl font-bold font-heading text-slate-900">www.vtoroemnenie.com</span>
              </div>
              <p className="text-slate-600 text-sm">Профессиональное второе мнение по радиологии от лицензированных специалистов</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Контакты</h4>
              <p className="text-slate-600 text-sm mb-2">Email: info@radiologykonsult.com</p>
              <p className="text-slate-600 text-sm">Телефон: +7 (800) 123-45-67</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Ссылки</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/blog')} className="text-slate-600 hover:text-teal-600">Блог</button></li>
                <li><a href="#doctors" className="text-slate-600 hover:text-teal-600">О нас</a></li>
                <li><a href="#" className="text-slate-600 hover:text-teal-600">Политика конфиденциальности</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8 text-center">
            <p className="text-slate-500 text-sm mb-2">
              <strong className="text-slate-600">Отказ от ответственности:</strong> Эта платформа предоставляет только второе мнение и не должна заменять первичную медицинскую консультацию.
            </p>
            <p className="text-slate-500 text-sm">© 2024 www.vtoroemnenie.com. Все права защищены.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
